import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MaskedInput from 'react-input-mask';
import { Row, Col, Card, Form, Input, Button, Alert, notification, Select } from 'antd';
import { PageContainer } from '@ant-design/pro-layout';
import definitionService from 'services/definition.service';
import branchService from 'services/branch.service';
import merchService from 'services/merch.service';
import { AuthContext, BranchContext } from 'context';

const { Option } = Select;

const BranchEdit = () => {
  const navigate = useNavigate();
  const context = useContext(AuthContext);
  const number = context.selectedMerch?.number;
  const { selectedBranch } = useContext(BranchContext);

  const [form] = Form.useForm();
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error] = useState(false);

  const getMerchantTypes = async () => {
    const { payload } = await merchService.getMerchantType();
    setTypes(payload);
  };

  const getCities = async () => {
    const { payload } = await definitionService.getDefinition('city');
    setCities(payload);
  };

  const getDistricts = async (cityId) => {
    const { payload } = await definitionService.getDefinition('district', { cityId });
    setDistricts(payload);
    form.resetFields(['DistrictId']);
  };

  const getNeighborhoods = async (districtId) => {
    const { payload } = await definitionService.getDefinition('neighborhood', { districtId });
    setNeighborhoods(payload);
    form.resetFields(['NeighborhoodId']);
  };

  useEffect(() => {
    if (selectedBranch) {
      (async () => {
        await getCities();
        await getMerchantTypes();
        if (selectedBranch?.CityId) {
          try {
            await getDistricts(selectedBranch?.CityId);
            if (selectedBranch?.DistrictId) await getNeighborhoods(selectedBranch?.DistrictId);
          } catch (branchError) {
            notification.error({
              message: 'İşlem Başarısız',
              description: branchError.message || 'Bir hata oluştu.',
            });
          }
        }
      })();
    } else {
      navigate('/subeler');
    }
  }, [selectedBranch]);

  const selectFilter = (input, option) => {
    return option.children.toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'));
  };

  const handleSubmitUpdateForm = async (values) => {
    setLoading(true);
    const gsm = values.Gsm.replace(/[^\d\\+]/g, '');
    if (number && selectedBranch?.BranchId) {
      const { Gsm, ...clonedObject } = values;

      const newValues = {
        BranchId: selectedBranch?.BranchId,
        ...clonedObject,
        Gsm: gsm,
      };

      try {
        await branchService.updateBranch(number, newValues);
        notification.success({
          message: 'İşlem Başarılı',
          description: 'Şube Güncellendi!',
        });
        setLoading(false);
        navigate(-1);
      } catch (updateBranchError) {
        notification.error({
          message: 'İşlem Başarısız',
          description: updateBranchError.message || 'Bir hata oluştu.',
        });
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      handleSubmitUpdateForm(values);
    } catch (err) {
      console.error('Validation Failed:', err);
    }
  };

  return (
    <PageContainer title="Şube Başvurusu Düzenme" onBack={() => navigate(-1)}>
      <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={selectedBranch}>
        {error && (
          <Form.Item>
            <Alert message={error} type="error" showIcon />
          </Form.Item>
        )}
        <Row gutter={[20, 20]}>
          <Col span={12}>
            <Card type="inner" title="Şube Bilgileri">
              <Form.Item
                name="BranchTitle"
                label="Şube Adı"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Input className="input__default" placeholder="Şube Adı" />
              </Form.Item>
              <Form.Item
                name="BranchCategoryList"
                label="Kategori"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Select style={{ width: '100%' }} placeholder="Kategori Seçiniz" mode="multiple">
                  {types?.map((type) => (
                    <Option key={type?.MerchantTypeId} value={type?.MerchantTypeId}>
                      {type.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="BranchContactPerson"
                label="Yetkili Kişi"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Input className="input__default" placeholder="Yetkili Kişi" />
              </Form.Item>
            </Card>
          </Col>
          <Col span={12}>
            <Card type="inner" title="İletişim Bilgileri">
              <Form.Item
                name="Gsm"
                label="İletişim No"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <MaskedInput mask="(999) 999-99-99">
                  {() => <Input className="input__default" placeholder="İletişim No" />}
                </MaskedInput>
              </Form.Item>
              <Form.Item
                name="Email"
                label="Email"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Input className="input__default" placeholder="Email" />
              </Form.Item>
            </Card>
          </Col>
          <Col span={24}>
            <Card type="inner" title="Adres Bilgileri">
              <Form.Item
                name="Address"
                label="Açık Adres"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Input.TextArea className="input__default" placeholder="Açık Adres" />
              </Form.Item>
              <Form.Item
                name="CityId"
                label="İl"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="İl Seçiniz"
                  filterOption={selectFilter}
                  onChange={(val) => getDistricts(val)}
                >
                  {cities?.map((c) => (
                    <Option key={c.Name} value={c.CityId}>
                      {c.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="DistrictId"
                label="İlçe"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Select
                  showSearch
                  placeholder="İlçe Seçiniz"
                  style={{ width: '100%' }}
                  onChange={(d) => getNeighborhoods(d)}
                  filterOption={selectFilter}
                >
                  {districts?.map((c) => (
                    <Option key={c.Name} value={c.DistrictId}>
                      {c.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="NeighborhoodId"
                label="Mahalle"
                rules={[{ required: true, message: 'Zorunlu Alan' }]}
              >
                <Select
                  showSearch
                  placeholder="Mahalle Seçiniz"
                  style={{ width: '100%' }}
                  filterOption={selectFilter}
                >
                  {neighborhoods?.map((n) => (
                    <Option key={n.Name} value={n.NeighborhoodId}>
                      {n.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Card>
          </Col>
        </Row>
        <Form.Item className="text-align-right">
          <Button
            className="button button__fill"
            type="primary"
            htmlType="submit"
            disabled={loading}
            loading={loading}
          >
            Kaydet
          </Button>
        </Form.Item>
      </Form>
    </PageContainer>
  );
};

export default BranchEdit;
